import * as echarts from 'echarts/core';
import {Component, Prop, Ref, Vue, Watch} from 'vue-property-decorator';

import {PieChart, PieSeriesOption} from 'echarts/charts';
import {
    GridComponent,
    GridComponentOption,
    LegendComponent,
    LegendComponentOption,
    TitleComponent,
    TitleComponentOption,
    TooltipComponent,
    TooltipComponentOption,
} from 'echarts/components';
import {CanvasRenderer} from 'echarts/renderers';

type ECOption = echarts.ComposeOption<PieSeriesOption | TooltipComponentOption | LegendComponentOption | TitleComponentOption | GridComponentOption>;

echarts.use([PieChart, TooltipComponent, LegendComponent, TitleComponent, GridComponent, CanvasRenderer]);

@Component
export default class DonutChartComponent extends Vue {
    @Prop({type: String, default: '400px'})
    readonly height!: string;

    @Prop({type: String, default: '100%'})
    readonly width!: string;

    @Ref('chartContainer') readonly chartContainer!: HTMLElement;

    private chartInstance: echarts.ECharts | null = null;
    private resizeObserver: ResizeObserver | null = null;
    private isMounted: boolean = false;

    chartOptions: ECOption = {
        tooltip: {
            trigger: 'item',
            formatter: '{a} <br/>{b}: {c} ({d}%)',
        },
        series: [
            {
                name: 'Status Breakdown',
                type: 'pie',
                radius: ['40%', '70%'],
                center: ['50%', '50%'],
                avoidLabelOverlap: true,
                data: [
                    {value: Math.round(Math.random() * 1000), name: 'Pass'},
                    {value: Math.round(Math.random() * 800), name: 'Fail'},
                    {value: Math.round(Math.random() * 600), name: 'N/A'},
                ],
            },
        ],
    };

    get containerStyle() {
        return {
            width: this.width,
            height: this.height,
        };
    }

    mounted() {
        this.isMounted = true;
        this.$nextTick(() => {
            this.initChart();
            this.setupResizeObserver();
        });
    }

    beforeDestroy() {
        this.isMounted = false;
        this.disposeChart();
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
    }

    @Watch('chartOptions', {deep: true})
    onInternalOptionsChanged(newOptions: ECOption) {
        if (this.chartInstance && this.isMounted) {
            this.chartInstance.setOption(newOptions, false);
        }
    }

    initChart(): void {
        if (!this.chartContainer) {
            console.error('ECharts: Chart container ref not found.');
            return;
        }
        this.disposeChart();
        try {
            this.chartInstance = echarts.init(this.chartContainer);
            if (this.chartOptions) {
                this.chartInstance.setOption(this.chartOptions);
            } else {
                console.warn('ECharts: Internal chartOptions are not defined.');
            }
        } catch (error) {
            console.error('ECharts initialization failed:', error);
            this.disposeChart();
        }
    }

    resizeChart(): void {
        if (this.chartInstance && this.isMounted) {
            this.$nextTick(() => {
                this.chartInstance?.resize();
            });
        }
    }

    disposeChart(): void {
        if (this.chartInstance) {
            this.chartInstance.dispose();
            this.chartInstance = null;
        }
    }

    setupResizeObserver(): void {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        this.resizeObserver = new ResizeObserver(() => {
            this.resizeChart();
        });
        if (this.chartContainer) {
            this.resizeObserver.observe(this.chartContainer);
        } else {
            console.error('ECharts: Chart container not found for ResizeObserver.');
        }
    }

    getChartInstance(): echarts.ECharts | null {
        return this.chartInstance;
    }

    updateChartData(newData: Array<{value: number; name: string}>) {
        if (!this.chartInstance || !this.isMounted) return;

        const newOptions = {
            ...this.chartOptions,
            series: [
                {
                    ...(this.chartOptions.series as PieSeriesOption[])[0],
                    data: newData,
                },
            ],
        };

        this.chartOptions = newOptions;
    }
}
